import { CompanyParametersQuery, CountryCollectionQuery } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, Weaver } from '@skylab-shared/core';
import { OrderDeliveryAddressModel, OrderRecipientAddressModel, OrderRecipientKind, OrderRecipientModel, OrdersValidationPatterns } from '@skylab-shared/dataModel';
import { OrderAddressZipCodeInvalidError } from '../violations/order-address-zip-code-invalid.error';
import { OrderRecipientCompanyNameEmptyError } from '../violations/order-recipient-company-name-empty.error';
import { OrderRecipientFirstNameEmptyError } from '../violations/order-recipient-first-name-empty.error';
import { OrderRecipientLastNameEmptyError } from '../violations/order-recipient-last-name-empty.error';

interface DocumentModelPart extends ModelBase {
}

export class OrderRecipientValidationBoPart extends BusinessObjectPart<DocumentModelPart> {
	public constructor(
		weaver: Weaver,
	) {
		super(weaver);
	}

	public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
		await super.initializeAsync(context);
		this.registerValidationFeatures();
	}

	private registerValidationFeatures(): void {
		this.engine.validation.addRule(
			[[OrderRecipientModel, 'companyName'], [OrderRecipientModel, 'kind']],
			[[OrderRecipientModel, 'companyName']],
			ctx => {
				if (ctx.source.kind === OrderRecipientKind.Individual) return true;
				return !!ctx.source.companyName;
			},
			OrderRecipientCompanyNameEmptyError
		);

		this.engine.validation.addRule(
			[[OrderRecipientModel, 'firstName'], [OrderRecipientModel, 'kind']],
			[[OrderRecipientModel, 'firstName']],
			ctx => {
				if (ctx.source.kind === OrderRecipientKind.Business) return true;
				return !!ctx.source.firstName;
			},
			OrderRecipientFirstNameEmptyError
		);

		this.engine.validation.addRule(
			[[OrderRecipientModel, 'lastName'], [OrderRecipientModel, 'kind']],
			[[OrderRecipientModel, 'lastName']],
			ctx => {
				if (ctx.source.kind === OrderRecipientKind.Business) return true;
				return !!ctx.source.lastName;
			},
			OrderRecipientLastNameEmptyError
		);

		this.engine.validation.addRule(
			[[OrderRecipientAddressModel, 'zipCode'], [OrderRecipientAddressModel, 'countryExternalId']],
			[[OrderRecipientAddressModel, 'zipCode']],
			ctx => this.checkZipCode(ctx.target.zipCode, ctx.target.countryExternalId!),
			OrderAddressZipCodeInvalidError
		);

		this.engine.validation.addRule(
			[[OrderDeliveryAddressModel, 'zipCode'], [OrderDeliveryAddressModel, 'countryExternalId']],
			[[OrderDeliveryAddressModel, 'zipCode']],
			ctx => this.checkZipCode(ctx.target.zipCode, ctx.target.countryExternalId!),
			OrderAddressZipCodeInvalidError
		);
	}

	private checkZipCode(zipCode: string, countryExternalId?: string): boolean {
		if (!zipCode) return true;

		const system = this.bo.localContext.getPreloaded(CompanyParametersQuery);
		const country = this.bo.localContext.firstPreloaded(CountryCollectionQuery, {externalId: countryExternalId});

		if (country?.id !== system?.systemCountryId) return true;

		return OrdersValidationPatterns.ZipCode.test(zipCode);
	}
}
